import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/admin/api";
import { Logo } from "@/components/comiq/Logo";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { Contour } from "@/components/comiq/Contour";
import { SurveyHeader } from "@/components/comiq/SurveyHeader";
import { AnswerInput } from "@/components/comiq/AnswerInput";
import { SectionHeader, BlockPrompt } from "@/components/comiq/SectionHeader";
import SurveyLanding from "@/pages/SurveyLanding";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Building2, Clock, ShieldCheck, AlertTriangle, Ban, CheckCircle2, ArrowRight, ArrowLeft, PencilLine } from "lucide-react";

const READ = "mx-auto max-w-[680px] px-5";
// Feature flag: respondent research-landing entry page (future capability).
// Default OFF -> /s/:token uses the existing respondent flow (intro -> consent -> ...).
const RESPONDENT_LANDING = process.env.REACT_APP_ENABLE_RESPONDENT_LANDING === "true";

export default function RespondentLink() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading|invalid|error|blocked|landing|intro|consent|question|review|done
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(null);
  const [agree, setAgree] = useState(false);
  const [idx, setIdx] = useState(0);
  const [saved, setSaved] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [entry, setEntry] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    const url = RESPONDENT_LANDING ? `/survey/${token}/landing` : `/survey/${token}`;
    api.get(url)
      .then((r) => {
        setMeta(r.data);
        setEntry(r.data);
        if (["expired", "revoked", "submitted", "withdrawn"].includes(r.data.status)) setPhase("blocked");
        else setPhase(RESPONDENT_LANDING ? "landing" : "intro");
      })
      .catch((e) => setPhase(e.response?.status === 404 ? "invalid" : "error"));
  }, [token]);

  const hydrate = (d) => {
    setItems(d.instrument.items);
    setAnswers(d.answers || {});
    setConsent(d.consent);
    setMeta((m) => ({ ...m, ...d }));
    if (!d.consent_accepted) { setPhase("consent"); return; }
    const firstUnanswered = d.instrument.items.findIndex((it) => d.answers?.[it.item_id] === undefined);
    setIdx(firstUnanswered === -1 ? 0 : firstUnanswered);
    setPhase("question");
  };

  const start = async () => {
    setBusy(true);
    try { const r = await api.post(`/survey/${token}/start`); hydrate(r.data); }
    catch (e) { setPhase("error"); setDone({ err: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const acceptConsent = async () => {
    setBusy(true);
    try {
      await api.post(`/survey/${token}/consent`);
      const first = items.findIndex((it) => answers[it.item_id] === undefined);
      setIdx(first === -1 ? 0 : first);
      setPhase("question");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan persetujuan"); } finally { setBusy(false); }
  };

  const persist = useCallback((itemId, value) => {
    setSaved(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/survey/${token}/answer`, { item_id: itemId, value })
        .then(() => setSaved(true))
        .catch(() => { setSaved(true); toast.error("Gagal menyimpan otomatis — periksa koneksi Anda"); });
    }, 350);
  }, [token]);

  const onAnswer = (value) => {
    const it = items[idx];
    setAnswers((a) => ({ ...a, [it.item_id]: value }));
    persist(it.item_id, value);
  };

  const submit = async () => {
    setBusy(true);
    try { const r = await api.post(`/survey/${token}/submit`); setDone(r.data); setPhase("done"); }
    catch (e) { setPhase("error"); setDone({ err: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen" style={{ background: COLORS.paper }}>
      <header className="border-b" style={{ borderColor: COLORS.line, background: COLORS.surfaceAlt }}>
        <div className="mx-auto flex max-w-[680px] items-center justify-between px-5 py-4">
          <Logo />
          <span className="text-xs font-medium" style={{ color: COLORS.textSoft }}>Undangan Survei</span>
        </div>
      </header>
      {children}
    </div>
  );

  // ---------- states ----------
  if (phase === "loading") return <Shell><p className={`${READ} py-14 text-sm`} style={{ color: COLORS.textSoft }} data-testid="link-loading">Memvalidasi tautan…</p></Shell>;

  if (phase === "invalid" || phase === "error") return (
    <Shell>
      <div className={`${READ} py-14`} data-testid="link-invalid">
        <Eyebrow>Tautan tidak valid</Eyebrow>
        <h1 className="font-serif-editorial mt-3 text-4xl" style={{ color: COLORS.ink }}>{phase === "invalid" ? "Tautan ini tidak dikenali." : "Terjadi kendala."}</h1>
        <p className="mt-4 text-base" style={{ color: COLORS.textSoft }}>{done?.err || "Periksa kembali tautan pada undangan Anda, atau hubungi koordinator riset di organisasi Anda."}</p>
      </div>
    </Shell>
  );

  if (phase === "blocked") return (
    <Shell>
      <div className={`${READ} py-14`}>
        <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border px-4 py-2 text-sm" style={{ borderColor: COLORS.line, background: COLORS.surfaceAlt, color: COLORS.ink }}>
          <Building2 size={16} style={{ color: COLORS.orangeInk }} /> Diundang oleh <strong className="font-semibold">{meta.organization}</strong>
        </div>
        <h1 className="font-serif-editorial text-4xl" style={{ color: COLORS.ink }}>Tautan ini sudah tidak aktif.</h1>
        <div className="mt-6 flex items-start gap-3 rounded-comiq border p-5" style={{ borderColor: COLORS.line, background: COLORS.surfaceAlt }} data-testid={`link-${meta.status}`}>
          {meta.status === "revoked" ? <Ban size={18} style={{ color: "#B42318" }} /> : meta.status === "submitted" ? <CheckCircle2 size={18} style={{ color: "#15803D" }} /> : <AlertTriangle size={18} style={{ color: "#B45309" }} />}
          <div className="text-sm" style={{ color: COLORS.textSoft }}>
            {meta.status === "expired" && "Masa berlaku tautan telah berakhir. Minta tautan baru kepada koordinator riset Anda."}
            {meta.status === "revoked" && "Undangan ini telah dicabut. Hubungi koordinator riset Anda bila ini tidak seharusnya terjadi."}
            {meta.status === "submitted" && "Terima kasih — jawaban Anda sudah terekam. Setiap undangan hanya dapat dikirim satu kali."}
          </div>
        </div>
      </div>
    </Shell>
  );

  if (phase === "landing") return <SurveyLanding data={entry} onStart={start} busy={busy} />;

  if (phase === "intro") return (
    <Shell>
      <main className="relative overflow-x-hidden">
        <Contour className="pointer-events-none absolute -right-24 top-8 hidden h-80 w-[520px] opacity-50 lg:block" />
        <section className="mx-auto w-full max-w-[760px] px-6 py-16 sm:px-8 sm:py-20" data-testid="survey-intro">
          <div className="mb-7 inline-flex max-w-full items-start gap-2.5 rounded-full border px-4 py-2 text-sm" style={{ borderColor: COLORS.line, background: COLORS.surface, color: COLORS.ink }} data-testid="intro-org-chip">
            <Building2 size={16} className="mt-0.5 shrink-0" style={{ color: COLORS.orangeInk }} />
            <span className="min-w-0 break-words">Diundang oleh <strong className="font-semibold">{meta.organization}</strong></span>
          </div>
          {meta.is_demo && <div className="mb-7 inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="demo-badge">Data demo — bukan survei produksi</div>}
          <Eyebrow className="block max-w-full break-words" data-testid="intro-study-label">{meta.study_title} · {meta.wave_label}</Eyebrow>
          <h1 className="font-serif-editorial mt-5 break-words text-[2.8rem] text-balance sm:text-6xl" style={{ color: COLORS.ink, letterSpacing: "-0.025em", lineHeight: 1.02 }} data-testid="intro-heading">
            Selamat datang di <em style={{ color: COLORS.orange }}>studi ComIQ</em>.
          </h1>
          <p className="mt-6 max-w-[620px] text-base sm:text-lg" style={{ color: COLORS.textSoft, lineHeight: 1.75 }} data-testid="intro-description">
            Survei ini singkat dan dapat dijeda. Progres Anda tersimpan otomatis — Anda bisa kembali ke tautan yang sama kapan saja tanpa perlu akun.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="intro-facts">
            {[{ icon: Clock, t: "± 11 menit", s: "Bisa dijeda & dilanjutkan" }, { icon: ShieldCheck, t: "Kerahasiaan", s: "Laporan selalu agregat" }, { icon: Building2, t: meta.participant_code, s: "Kode partisipan Anda" }].map((f, i) => (
              <div key={f.t} className="min-w-0 rounded-[20px] border p-5 shadow-[0_1px_0_rgba(23,20,15,0.03)]" style={{ borderColor: COLORS.line, background: COLORS.surface }} data-testid={`intro-fact-${i + 1}`}>
                <f.icon size={17} style={{ color: COLORS.orangeInk }} />
                <div className="mt-3 break-words font-mono text-sm font-semibold" style={{ color: COLORS.ink }}>{f.t}</div>
                <div className="mt-1 text-xs leading-5" style={{ color: COLORS.textSoft }}>{f.s}</div>
              </div>
            ))}
          </div>
          <button onClick={start} disabled={busy} className="btn-comiq btn-accent mt-10 text-base disabled:opacity-50" style={{ boxShadow: "0 10px 24px rgba(242,84,27,0.18)" }} data-testid="start-survey-btn">
            {busy ? "Memuat…" : <>Mulai survei <ArrowRight size={17} /></>}
          </button>
          <p className="mt-7 max-w-[560px] text-[13px] leading-6" style={{ color: COLORS.textSoft }} data-testid="intro-access-note">
            Verifikasi ulang (kode OTP email) hanya diminta bila tautan kedaluwarsa/dicabut — tidak pada setiap pembukaan.
          </p>
        </section>
      </main>
    </Shell>
  );

  if (phase === "consent") return (
    <Shell>
      <div className={`${READ} py-12`} data-testid="consent-screen">
        <Eyebrow>Persetujuan</Eyebrow>
        <h1 className="font-serif-editorial mt-3 text-3xl sm:text-4xl" style={{ color: COLORS.ink }}>{consent?.title}</h1>
        <div className="mt-5 whitespace-pre-line rounded-comiq border bg-comiq-surface-alt p-5 text-sm" style={{ borderColor: COLORS.line, color: COLORS.textSoft, lineHeight: 1.7 }}>{consent?.body}</div>
        <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm" style={{ color: COLORS.ink }}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="consent-checkbox" />
          <span>Saya memahami dan menyetujui untuk berpartisipasi.</span>
        </label>
        <button onClick={acceptConsent} disabled={!agree || busy} className="btn-comiq btn-accent mt-6 disabled:opacity-40" data-testid="consent-accept-btn">Setuju &amp; lanjutkan <ArrowRight size={16} /></button>
      </div>
    </Shell>
  );

  if (phase === "question") {
    const it = items[idx];
    const val = answers[it.item_id];
    const prev = idx > 0 ? items[idx - 1] : null;
    const showSection = !prev || prev.section_id !== it.section_id;
    const showPrompt = Boolean((it.group_prompt || "").trim()) && (!prev || (prev.group_prompt || "").trim() !== (it.group_prompt || "").trim());
    return (
      <div className="min-h-screen" style={{ background: COLORS.paper }}>
        <SurveyHeader current={idx + 1} total={items.length} saved={saved} />
        <main className="relative overflow-hidden">
          <div className={`${READ} py-10`} data-testid="question-screen">
            {showSection && (
              <SectionHeader
                label={it.section_label}
                title={it.section_title}
                description={it.section_description}
                instruction={it.section_instruction}
                sectionCode={it.section_code}
              />
            )}
            {showPrompt && <BlockPrompt prompt={it.group_prompt} />}
            <h2 className="mt-3 text-2xl font-semibold" style={{ color: COLORS.ink, lineHeight: 1.4 }} data-testid="question-wording">{it.wording}</h2>
            <div className="mt-7">
              <AnswerInput item={it} value={val} onAnswer={onAnswer} />
            </div>
            <div className="mt-9 flex items-center justify-between">
              <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} className="btn-comiq btn-line disabled:opacity-40" data-testid="prev-btn"><ArrowLeft size={16} /> Kembali</button>
              {idx < items.length - 1 ? (
                <button onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))} className="btn-comiq btn-accent" data-testid="next-btn">Lanjut <ArrowRight size={16} /></button>
              ) : (
                <button onClick={() => setPhase("review")} className="btn-comiq btn-accent" data-testid="to-review-btn">Tinjau jawaban <ArrowRight size={16} /></button>
              )}
            </div>
          </div>
          <div className="flex justify-center pb-8 opacity-60"><Contour className="h-20 w-72" tone="ink" /></div>
        </main>
      </div>
    );
  }

  if (phase === "review") {
    const empty = items.filter((it) => answers[it.item_id] === undefined).length;
    const label = (v) => v === undefined ? { t: "Belum diisi", c: "#B45309", bg: "#FEF3C7" } : v === "unsure" ? { t: "Tidak yakin", c: "#57534E", bg: "#EEEAE0" } : { t: `Jawaban: ${v}`, c: "#15803D", bg: "#DCFCE7" };
    return (
      <Shell>
        <div className={`${READ} py-12`} data-testid="review-screen">
          <Eyebrow>Tinjau</Eyebrow>
          <h1 className="font-serif-editorial mt-3 text-3xl sm:text-4xl" style={{ color: COLORS.ink }}>Tinjau sebelum mengirim</h1>
          <p className="mt-3 text-sm" style={{ color: COLORS.textSoft }}>
            {empty === 0 ? "Semua pertanyaan telah diisi." : `${empty} pertanyaan belum diisi. "Belum diisi" berbeda dari "Tidak yakin" — Anda tetap dapat mengirim.`}
          </p>
          <div className="mt-6 space-y-2">
            {items.map((it, i) => {
              const s = label(answers[it.item_id]);
              return (
                <div key={it.item_id} className="flex items-center gap-3 rounded-comiq border bg-comiq-surface-alt px-4 py-3" style={{ borderColor: COLORS.line }} data-testid={`review-${it.stable_item_code}`}>
                  <span className="font-mono text-xs font-bold" style={{ color: COLORS.ink }}>{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm" style={{ color: COLORS.ink }}>{it.wording}</span>
                  <span className="rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: s.bg, color: s.c }}>{s.t}</span>
                  <button onClick={() => { setIdx(i); setPhase("question"); }} className="rounded-full border p-1.5" style={{ borderColor: COLORS.line }} data-testid={`edit-${it.stable_item_code}`} title="Ubah"><PencilLine size={13} style={{ color: COLORS.ink }} /></button>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => { setIdx(items.length - 1); setPhase("question"); }} className="btn-comiq btn-line" data-testid="back-to-questions-btn"><ArrowLeft size={16} /> Kembali</button>
            <button onClick={submit} disabled={busy} className="btn-comiq btn-accent disabled:opacity-50" data-testid="submit-btn">{busy ? "Mengirim…" : "Kirim jawaban"}</button>
          </div>
        </div>
      </Shell>
    );
  }

  if (phase === "done") return (
    <Shell>
      <div className={`${READ} py-16 text-center`} data-testid="done-screen">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ background: "#DCFCE7" }}><CheckCircle2 size={32} style={{ color: "#15803D" }} /></div>
        <h1 className="font-serif-editorial mt-6 text-4xl" style={{ color: COLORS.ink }}>Terima kasih.</h1>
        <p className="mt-4 text-base" style={{ color: COLORS.textSoft }}>
          {done?.already_submitted ? "Jawaban Anda sudah terekam sebelumnya — setiap undangan hanya dapat dikirim satu kali." : "Jawaban Anda telah terkirim dan terekam."}
        </p>
        <p className="mt-2 font-mono text-sm" style={{ color: COLORS.textSoft }}>Kode partisipan: {done?.participant_code}</p>
      </div>
    </Shell>
  );

  return null;
}
